import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { ArrowLeft, Plus, MapPin, Clock, Trash2, Edit, Save, Search, Camera, GripVertical, Navigation, X } from "lucide-react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import StopSearch from "@/components/StopSearch";
import ImageUpload from "@/components/ImageUpload";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CATEGORIES = [
  { value: 'dive_bar', label: 'Dive Bar', emoji: '🍺' },
  { value: 'restaurant', label: 'Restaurant', emoji: '🍔' },
  { value: 'landmark', label: 'Landmark', emoji: '🗿' },
  { value: 'hotel', label: 'Hotel', emoji: '🏨' },
  { value: 'gas_station', label: 'Gas Station', emoji: '⛽' },
  { value: 'rest_stop', label: 'Rest Stop', emoji: '☕' },
  { value: 'city', label: 'Town', emoji: '🏙️' },
];

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: -1, label: 'End of Day' },
];

// Sortable Stop Component
const SortableStop = ({ stop, index, getCategoryEmoji, formatDuration, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stop.stop_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`stop-${stop.stop_id}`}
      className={`glass rounded-xl p-4 flex items-center gap-3 ${isDragging ? 'shadow-lg ring-2 ring-bronze' : ''}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1"
      >
        <GripVertical className="w-5 h-5 text-gray-500" />
      </div>

      {/* Stop number */}
      <div className="flex items-center justify-center w-8 h-8 bg-bronze/20 rounded-full text-bronze font-bold text-sm flex-shrink-0">
        {index + 1}
      </div>
      
      {/* Stop icon/image */}
      {stop.images?.length > 0 ? (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
          <img src={stop.images[0]} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="flex items-center justify-center w-12 h-12 bg-white/5 rounded-lg text-gray-500 flex-shrink-0">
          <span className="text-2xl">{getCategoryEmoji(stop.category)}</span>
        </div>
      )}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{stop.name}</span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1 text-bronze">
            <Clock className="w-3 h-3" />
            {formatDuration(stop.estimated_time)}
          </span>
        </div>
      </div>
      
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(stop)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-400 hover:text-red-300"
          onClick={() => onDelete(stop.stop_id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const TripBuilder = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { user } = useAuth();

  const [currentTripId, setCurrentTripId] = useState(tripId || null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [startingLocation, setStartingLocation] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Stop modal state
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [stopName, setStopName] = useState("");
  const [stopCategory, setStopCategory] = useState("dive_bar");
  const [stopAddress, setStopAddress] = useState("");
  const [stopNotes, setStopNotes] = useState("");
  const [stopTime, setStopTime] = useState(30);
  const [stopLat, setStopLat] = useState(0);
  const [stopLng, setStopLng] = useState(0);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [stopImages, setStopImages] = useState([]);

  useEffect(() => {
    if (tripId) {
      setCurrentTripId(tripId);
      fetchTrip();
    }
  }, [tripId]);

  const fetchTrip = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/trips/${tripId}`);
      const trip = response.data;
      setTitle(trip.title);
      setDescription(trip.description || "");
      setCoverImage(trip.cover_image || "");
      setStartingLocation(trip.starting_location || null);
      setIsPublic(trip.is_public);
      setStops(trip.stops || []);
    } catch (error) {
      toast.error("Failed to load trip");
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Please enter a trip title");
      return;
    }

    setSaving(true);
    try {
      if (currentTripId) {
        await axios.put(`${API}/trips/${currentTripId}`, {
          title,
          description,
          cover_image: coverImage,
          starting_location: startingLocation,
          is_public: isPublic
        });
        toast.success("Trip saved!");
        navigate('/dashboard');
      } else {
        const response = await axios.post(`${API}/trips`, {
          title,
          description,
          cover_image: coverImage,
          starting_location: startingLocation,
          is_public: isPublic
        });
        const newTripId = response.data.trip_id;
        setCurrentTripId(newTripId);
        toast.success("Trip created! Now add your stops.");
        // Update URL without full reload
        window.history.replaceState(null, '', `/trip/${newTripId}/edit`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save trip");
    } finally {
      setSaving(false);
    }
  };

  const openStopModal = (stop = null) => {
    if (stop) {
      setEditingStop(stop);
      setStopName(stop.name);
      setStopCategory(stop.category);
      setStopAddress(stop.address || "");
      setStopNotes(stop.notes || "");
      setStopTime(stop.estimated_time || 30);
      setStopLat(stop.coordinates?.lat || 0);
      setStopLng(stop.coordinates?.lng || 0);
      setStopImages(stop.images || []);
      setSelectedPlace({
        lat: stop.coordinates?.lat,
        lng: stop.coordinates?.lng,
        address: stop.address
      });
    } else {
      setEditingStop(null);
      setStopName("");
      setStopCategory("dive_bar");
      setStopAddress("");
      setStopNotes("");
      setStopTime(30);
      setStopLat(0);
      setStopLng(0);
      setStopImages([]);
      setSelectedPlace(null);
    }
    setStopModalOpen(true);
  };

  const handleSaveStop = async () => {
    if (!stopName.trim()) {
      toast.error("Please enter a stop name");
      return;
    }

    if (!selectedPlace && !editingStop) {
      toast.error("Please search and select a location");
      return;
    }

    // If no trip yet, create one first
    if (!currentTripId) {
      if (!title.trim()) {
        toast.error("Please enter a trip title first");
        return;
      }
      
      try {
        const tripResponse = await axios.post(`${API}/trips`, {
          title,
          description,
          is_public: isPublic
        });
        const newTripId = tripResponse.data.trip_id;
        setCurrentTripId(newTripId);
        window.history.replaceState(null, '', `/trip/${newTripId}/edit`);
        
        // Now save the stop
        await saveStopToTrip(newTripId);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to create trip");
      }
    } else {
      await saveStopToTrip(currentTripId);
    }
  };

  const saveStopToTrip = async (tripIdToUse) => {
    try {
      const stopData = {
        trip_id: tripIdToUse,
        name: stopName,
        category: stopCategory,
        coordinates: {
          lat: stopLat || selectedPlace?.lat || 0,
          lng: stopLng || selectedPlace?.lng || 0
        },
        address: stopAddress || selectedPlace?.address,
        notes: stopNotes,
        estimated_time: stopTime,
        order: editingStop ? editingStop.order : stops.length,
        images: stopImages
      };

      if (editingStop) {
        await axios.put(`${API}/stops/${editingStop.stop_id}`, stopData);
        setStops(stops.map(s => s.stop_id === editingStop.stop_id ? { ...s, ...stopData } : s));
        toast.success("Stop updated!");
      } else {
        const response = await axios.post(`${API}/stops`, stopData);
        setStops([...stops, response.data]);
        toast.success("Stop added!");
      }
      setStopModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save stop");
    }
  };

  const handleDeleteStop = async (stopId) => {
    try {
      await axios.delete(`${API}/stops/${stopId}`);
      setStops(stops.filter(s => s.stop_id !== stopId));
      toast.success("Stop removed");
    } catch (error) {
      toast.error("Failed to delete stop");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = stops.findIndex(s => s.stop_id === active.id);
      const newIndex = stops.findIndex(s => s.stop_id === over.id);
      
      const newStops = arrayMove(stops, oldIndex, newIndex).map((stop, index) => ({
        ...stop,
        order: index
      }));
      
      setStops(newStops);
      
      // Update order in backend
      if (currentTripId) {
        try {
          await axios.put(`${API}/trips/${currentTripId}/reorder`, {
            stop_ids: newStops.map(s => s.stop_id)
          });
        } catch (error) {
          console.error("Failed to save order:", error);
        }
      }
    }
  };

  const getCategoryEmoji = (category) => {
    return CATEGORIES.find(c => c.value === category)?.emoji || '📍';
  };

  const formatDuration = (minutes) => {
    if (minutes === -1) return 'End of Day';
    if (minutes >= 1440) return 'Next day';
    if (minutes >= 720) return 'Overnight';
    if (minutes < 60) return `${minutes} min`;
    if (minutes === 60) return '1 hour';
    if (minutes < 120) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    return `${Math.floor(minutes / 60)} hours`;
  };

  // Group stops by day based on "End of Day" markers
  const getStopsByDay = () => {
    const sortedStops = [...stops].sort((a, b) => a.order - b.order);
    const days = [];
    let currentDay = [];
    let dayNumber = 1;

    sortedStops.forEach((stop, idx) => {
      currentDay.push({ ...stop, dayNumber });
      
      // If this stop is "End of Day", start a new day for subsequent stops
      if (stop.estimated_time === -1 && idx < sortedStops.length - 1) {
        days.push({ dayNumber, stops: currentDay });
        dayNumber++;
        currentDay = [];
      }
    });

    // Push remaining stops
    if (currentDay.length > 0) {
      days.push({ dayNumber, stops: currentDay });
    }

    return days;
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-bronze">Loading...</div>
      </div>
    );
  }

  return (
    <div className="mobile-container asphalt-texture min-h-screen p-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          data-testid="back-btn"
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={() => navigate(currentTripId ? `/trip/${currentTripId}` : '/dashboard')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-oswald text-xl font-bold uppercase">
          {currentTripId ? 'Edit Ride' : 'Plan New Ride'}
        </h1>
      </div>

      {/* Trip Details */}
      <div className="space-y-4 mb-6">
        <div className="space-y-2">
          <Label className="text-xs text-gray-400 uppercase">Trip Name</Label>
          <Input
            data-testid="trip-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Weekend Dive Bar Crawl"
            className="h-12 bg-[#121212] border-white/10 focus:border-bronze text-lg font-semibold"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-gray-400 uppercase">Description (optional)</Label>
          <Textarea
            data-testid="trip-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="The best dive bars from LA to San Diego..."
            className="bg-[#121212] border-white/10 focus:border-bronze min-h-[60px]"
          />
        </div>

        {/* Starting Location */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-400 uppercase flex items-center gap-2">
            <Navigation className="w-3 h-3" />
            Starting Location
          </Label>
          <PlaceAutocomplete
            onPlaceSelect={(place) => setStartingLocation(place)}
            placeholder="Where does your ride begin?"
            value={startingLocation?.name || ""}
          />
          {startingLocation && (
            <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30 flex items-center justify-between">
              <div>
                <p className="font-semibold text-white text-sm">{startingLocation.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{startingLocation.address}</p>
              </div>
              <button
                type="button"
                onClick={() => setStartingLocation(null)}
                className="text-gray-500 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Cover Photo */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-400 uppercase flex items-center gap-2">
            <Camera className="w-3 h-3" />
            Cover Photo (optional)
          </Label>
          <ImageUpload
            images={coverImage ? [coverImage] : []}
            onImagesChange={(images) => setCoverImage(images[0] || "")}
            maxImages={1}
            folder="trips/covers"
          />
        </div>

        <div className="flex items-center justify-between glass rounded-xl p-4">
          <div>
            <p className="font-medium text-sm">Make Public</p>
            <p className="text-xs text-gray-500">Share with the community</p>
          </div>
          <Switch
            data-testid="trip-public-switch"
            checked={isPublic}
            onCheckedChange={setIsPublic}
          />
        </div>
      </div>

      {/* Stops Section - Always visible */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-oswald text-lg uppercase text-gray-300">Stops Along The Way</h2>
          <span className="text-xs text-gray-500">{stops.length} stops</span>
        </div>

        <Button
          data-testid="add-stop-btn"
          variant="outline"
          className="w-full h-14 border-bronze/40 hover:bg-bronze/10 text-bronze font-oswald uppercase tracking-wider"
          onClick={() => openStopModal()}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Stop
        </Button>

        {stops.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center">
            <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-medium">No stops yet</p>
            <p className="text-gray-600 text-xs mt-1">Add your first dive bar, restaurant, or landmark!</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={stops.sort((a, b) => a.order - b.order).map(s => s.stop_id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {getStopsByDay().map((day, dayIdx) => (
                  <div key={day.dayNumber}>
                    {/* Day Header - only show if there are multiple days */}
                    {getStopsByDay().length > 1 && (
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px flex-1 bg-bronze/30" />
                        <span className="font-oswald text-sm uppercase tracking-wider text-bronze">
                          Day {day.dayNumber}
                        </span>
                        <div className="h-px flex-1 bg-bronze/30" />
                      </div>
                    )}
                    <div className="space-y-2">
                      {day.stops.map((stop) => {
                        // Calculate display index within the full sorted list
                        const sortedStops = stops.sort((a, b) => a.order - b.order);
                        const globalIndex = sortedStops.findIndex(s => s.stop_id === stop.stop_id);
                        return (
                          <SortableStop
                            key={stop.stop_id}
                            stop={stop}
                            index={globalIndex}
                            getCategoryEmoji={getCategoryEmoji}
                            formatDuration={formatDuration}
                            onEdit={openStopModal}
                            onDelete={handleDeleteStop}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Save Button */}
      <div className="mt-8 mb-8">
        <Button
          data-testid="save-trip-btn"
          className="w-full h-14 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider text-lg"
          onClick={handleSave}
          disabled={saving || !title.trim()}
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : (currentTripId ? 'Save Trip' : 'Create Trip')}
        </Button>
      </div>

      {/* Stop Modal */}
      <Dialog open={stopModalOpen} onOpenChange={setStopModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-oswald uppercase">
              {editingStop ? 'Edit Stop' : 'Add Stop'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2">
            {/* Place Search */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-400 uppercase flex items-center gap-2">
                <Search className="w-3 h-3" />
                Search Location
              </Label>
              <StopSearch
                onPlaceSelect={(place) => {
                  setStopName(place.name);
                  setStopAddress(place.address);
                  setStopLat(place.lat);
                  setStopLng(place.lng);
                  setSelectedPlace(place);
                  // Auto-detect category from place types
                  if (place.types) {
                    if (place.types.includes("bar") || place.types.includes("night_club")) {
                      setStopCategory("dive_bar");
                    } else if (place.types.includes("restaurant") || place.types.includes("food") || place.types.includes("cafe")) {
                      setStopCategory("restaurant");
                    } else if (place.types.includes("lodging") || place.types.includes("hotel")) {
                      setStopCategory("hotel");
                    } else if (place.types.includes("gas_station")) {
                      setStopCategory("gas_station");
                    } else if (place.types.includes("locality") || place.types.includes("administrative_area_level_3") || place.types.includes("sublocality")) {
                      setStopCategory("city");
                    }
                  }
                }}
                placeholder="dive bars near Moab, restaurants in..."
                initialValue={stopName}
                initialLocation={startingLocation ? { lat: startingLocation.lat, lng: startingLocation.lng } : null}
              />
            </div>

            {/* Category & Duration in a row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-gray-400 uppercase">Category</Label>
                <Select value={stopCategory} onValueChange={setStopCategory}>
                  <SelectTrigger data-testid="stop-category-select" className="h-10 bg-[#121212] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.emoji} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-gray-400 uppercase">Duration</Label>
                <Select value={stopTime.toString()} onValueChange={(v) => setStopTime(parseInt(v))}>
                  <SelectTrigger data-testid="stop-duration-select" className="h-10 bg-[#121212] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 max-h-[300px]">
                    {DURATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value.toString()}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-gray-400 uppercase">Notes (optional)</Label>
              <Textarea
                data-testid="stop-notes-input"
                value={stopNotes}
                onChange={(e) => setStopNotes(e.target.value)}
                placeholder="Best jukebox in town..."
                className="bg-[#121212] border-white/10 min-h-[50px]"
              />
            </div>

            {/* Image Upload - Collapsible */}
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer text-xs text-gray-400 uppercase hover:text-gray-300">
                <Camera className="w-3 h-3" />
                Photos (optional)
                <span className="ml-auto text-[10px]">tap to expand</span>
              </summary>
              <div className="mt-2">
                <ImageUpload
                  images={stopImages}
                  onImagesChange={setStopImages}
                  maxImages={5}
                  folder={`stops/${currentTripId || 'new'}`}
                />
              </div>
            </details>

            <Button
              data-testid="save-stop-btn"
              className="w-full h-11 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider"
              onClick={handleSaveStop}
            >
              {editingStop ? 'Update Stop' : 'Add Stop'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TripBuilder;
